import { z } from 'zod';
import { Experience } from '../../../types/experience';
import { ExperienceType } from '../../../enums/experience';

export const getObjectSchema = (
    schema: z.ZodTypeAny,
): z.ZodObject<z.ZodRawShape> | null => {
    if (schema instanceof z.ZodObject) {
        return schema;
    }

    if (schema instanceof z.ZodEffects) {
        return getObjectSchema(schema.innerType());
    }

    return null;
};

export const getPartialObjectSchema = (schema: z.ZodTypeAny) => {
    const objectSchema = getObjectSchema(schema);
    return objectSchema ? objectSchema.partial() : schema;
};

export function pickStepValues(
    schema: z.ZodTypeAny,
    source: Partial<Experience>,
) {
    const objectSchema = getObjectSchema(schema);
    if (!objectSchema) return {};

    const picked = pickNested(objectSchema, source) ?? {};
    const parsed = objectSchema.partial().safeParse(picked);

    return parsed.success ? parsed.data : {};
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Combines the parsed values of each step into a single payload.
 *
 * Nested objects are merged recursively so that steps which each own part of
 * `details` (amenities, availability, pricing) don't overwrite each other.
 */
export const mergeStepValues = (
    target: Record<string, unknown>,
    source: Record<string, unknown>,
): Record<string, unknown> => {
    const merged: Record<string, unknown> = { ...target };

    Object.entries(source).forEach(([key, value]) => {
        const existing = merged[key];

        merged[key] =
            isPlainObject(existing) && isPlainObject(value)
                ? mergeStepValues(existing, value)
                : value;
    });

    return merged;
};

export const prepareExperiencePayload = (
    data: Partial<Experience>,
    type: ExperienceType,
): Partial<Experience> & { type: ExperienceType } => {
    return {
        ...Object.fromEntries(
            Object.entries(data).map(([key, value]) => [
                key,
                value === '' ? undefined : value,
            ]),
        ),
        type,
    };
};

const pickNested = (
    currentSchema: z.ZodTypeAny,
    currentSource: unknown,
): unknown => {
    if (currentSource == null) return undefined;

    if (currentSchema instanceof z.ZodEffects) {
        return pickNested(currentSchema.innerType(), currentSource);
    }

    if (currentSchema instanceof z.ZodObject) {
        const shape = currentSchema.shape;
        const srcObj =
            typeof currentSource === 'object' && currentSource !== null
                ? (currentSource as Record<string, unknown>)
                : {};
        const out: Record<string, unknown> = {};
        for (const key of Object.keys(shape)) {
            const picked = pickNested(shape[key], srcObj[key]);
            if (picked !== undefined) {
                out[key] = picked;
            }
        }
        return Object.keys(out).length ? out : undefined;
    }

    return currentSource;
};
